import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { primaryLanguages, secondaryLanguages } from '../locales/index';

/**
 * Sprachauswahl-Komponente
 * 
 * @param {Object} props
 * @param {'dropdown' | 'buttons' | 'flags' | 'hybrid'} props.variant - Anzeigestil
 * @param {Object} props.style - Custom Styles
 */
// Suchnamen je Sprache (klein geschrieben): deutsch + englisch, damit „niederl", „dutch" oder „türk" treffen
const SUCHNAMEN = {
  de: 'deutsch german', en: 'englisch english', fr: 'französisch franzoesisch french', es: 'spanisch spanish',
  it: 'italienisch italian', tr: 'türkisch tuerkisch turkish', pl: 'polnisch polish', ru: 'russisch russian',
  pt: 'portugiesisch portuguese', nl: 'niederländisch niederlaendisch holländisch hollaendisch dutch nederlands',
  uk: 'ukrainisch ukrainian', ar: 'arabisch arabic', zh: 'chinesisch chinese', ja: 'japanisch japanese',
  ko: 'koreanisch korean', hr: 'kroatisch croatian', sr: 'serbisch serbian', ro: 'rumänisch rumaenisch romanian',
  fa: 'persisch farsi persian', vi: 'vietnamesisch vietnamese', el: 'griechisch greek', bg: 'bulgarisch bulgarian',
  cs: 'tschechisch czech',
};

const LanguageSelector = ({ variant = 'dropdown', style = {}, dark = false }) => {
  const { t, language, setLanguage, availableLanguages, languageNames } = useTranslation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Flaggen-Emojis für Sprachen
  const flags = {
    de: '🇩🇪',
    en: '🇬🇧',
    fr: '🇫🇷',
    es: '🇪🇸',
    tr: '🇹🇷',
    pl: '🇵🇱',
    ru: '🇷🇺',
    uk: '🇺🇦',
    ar: '🇸🇦',
    zh: '🇨🇳',
    it: '🇮🇹',
    pt: '🇵🇹',
    nl: '🇳🇱',
    ja: '🇯🇵',
    ko: '🇰🇷',
    hr: '🇭🇷',
    sr: '🇷🇸',
    ro: '🇷🇴',
    fa: '🇮🇷',
    vi: '🇻🇳',
    el: '🇬🇷',
    bg: '🇧🇬',
    cs: '🇨🇿',
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
        setSearchTerm('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (dropdownOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [dropdownOpen]);

  // Filter languages based on search term
  const filteredSecondaryLanguages = secondaryLanguages.filter((lang) => {
    const name = languageNames[lang] || lang;
    return name.toLowerCase().includes(searchTerm.toLowerCase()) ||
           lang.toLowerCase().includes(searchTerm.toLowerCase());
  });

  if (variant === 'hybrid') {
    const isSecondarySelected = secondaryLanguages.includes(language);
    
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, ...style }}>
        {/* DE und EN als prominente Buttons */}
        {primaryLanguages.map((lang) => (
          <button
            key={lang}
            onClick={() => {
              setLanguage(lang);
              setDropdownOpen(false);
            }}
            style={{
              padding: '8px 16px',
              borderRadius: 6,
              border: language === lang ? '2px solid #1976d2' : '1px solid #ccc',
              background: language === lang ? '#e3f2fd' : '#fff',
              cursor: 'pointer',
              fontWeight: language === lang ? 'bold' : 'normal',
              fontSize: 14,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              transition: 'all 0.2s ease',
            }}
          >
            <span style={{ fontSize: 18 }}>{flags[lang]}</span>
            {lang.toUpperCase()}
          </button>
        ))}

        {/* Dropdown für weitere Sprachen */}
        <div ref={dropdownRef} style={{ position: 'relative' }}>
          <button
            onClick={() => setDropdownOpen(!dropdownOpen)}
            style={{
              padding: '8px 12px',
              borderRadius: 6,
              border: isSecondarySelected ? '2px solid #1976d2' : '1px solid #ccc',
              background: isSecondarySelected ? '#e3f2fd' : '#fff',
              cursor: 'pointer',
              fontSize: 14,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontWeight: isSecondarySelected ? 'bold' : 'normal',
              transition: 'all 0.2s ease',
            }}
          >
            {isSecondarySelected ? (
              <>
                <span style={{ fontSize: 18 }}>{flags[language]}</span>
                {languageNames[language]?.replace(/^[^\s]+\s/, '') || language}
              </>
            ) : (
              <>
                <span style={{ fontSize: 16 }}>🌐</span>
                Mehr...
              </>
            )}
            <span style={{ 
              fontSize: 10, 
              marginLeft: 4,
              transform: dropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease',
            }}>
              ▼
            </span>
          </button>

          {dropdownOpen && (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                right: 0,
                marginTop: 4,
                background: '#fff',
                border: '1px solid #ccc',
                borderRadius: 8,
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                zIndex: 1000,
                minWidth: 200,
                maxHeight: 320,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {/* Suchfeld */}
              <div style={{ padding: 8, borderBottom: '1px solid #eee' }}>
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={t('ui.lang.searchPlaceholder')}
                  style={{
                    width: '100%',
                    padding: '8px 10px',
                    borderRadius: 6,
                    border: '1px solid #ddd',
                    fontSize: 14,
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              {/* Sprachliste */}
              <div style={{ overflowY: 'auto', maxHeight: 260 }}>
                {filteredSecondaryLanguages.length === 0 ? (
                  <div style={{ padding: 12, textAlign: 'center', color: '#999' }}>
                    {t('ui.lang.noneFound')}
                  </div>
                ) : (
                  filteredSecondaryLanguages.map((lang) => (
                    <button
                      key={lang}
                      onClick={() => {
                        setLanguage(lang);
                        setDropdownOpen(false);
                        setSearchTerm('');
                      }}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: 'none',
                        background: language === lang ? '#e3f2fd' : 'transparent',
                        cursor: 'pointer',
                        fontSize: 14,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        textAlign: 'left',
                        transition: 'background 0.15s ease',
                      }}
                      onMouseEnter={(e) => {
                        if (language !== lang) e.target.style.background = '#f5f5f5';
                      }}
                      onMouseLeave={(e) => {
                        if (language !== lang) e.target.style.background = 'transparent';
                      }}
                    >
                      <span style={{ fontSize: 20 }}>{flags[lang] || '🌐'}</span>
                      <span style={{ fontWeight: language === lang ? 'bold' : 'normal' }}>
                        {languageNames[lang]?.replace(/^[^\s]+\s/, '') || lang}
                      </span>
                      {language === lang && (
                        <span style={{ marginLeft: 'auto', color: '#1976d2' }}>✓</span>
                      )}
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // 'list' (Setup, Schritt 1): Suchfeld + alle Sprachen offen im Fenster — keine Aufklappliste,
  // die in einem kleinen Dialog abgeschnitten wird. Gefunden wird der Name in der Sprache selbst,
  // auf Deutsch oder Englisch („Nederlands", „Niederländisch", „Dutch") und das Kürzel.
  if (variant === 'list') {
    // ohne Akzente/Umlaute vergleichen: „nederländs" findet „Nederlands", „turk" findet „Türkçe"
    const glatt = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
    const q = glatt(searchTerm.trim());
    const alle = [...primaryLanguages, ...secondaryLanguages.filter((l) => !primaryLanguages.includes(l))];
    const treffer = alle.filter((lang) => !q
      || glatt(languageNames[lang]).includes(q)
      || lang.toLowerCase() === q
      || glatt(SUCHNAMEN[lang]).includes(q));
    const farben = dark
      ? { text: '#fff', rand: '#444', feld: '#1b1f27', aktiv: '#1c2b3f' }
      : { text: '#23272f', rand: '#ccc', feld: '#fff', aktiv: '#e3f2fd' };
    return (
      <div data-testid="lang-list" style={style}>
        <input
          data-testid="lang-search"
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder={`🔎 ${t('common.search')}`}
          style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: `1px solid ${farben.rand}`, fontSize: 15,
            outline: 'none', boxSizing: 'border-box', background: farben.feld, color: farben.text }}
        />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 10 }}>
          {treffer.map((lang) => (
            <button
              key={lang}
              type="button"
              data-testid={`lang-${lang}`}
              aria-pressed={language === lang}
              onClick={() => { setLanguage(lang); setSearchTerm(''); }}
              style={{ padding: '10px 10px', borderRadius: 8, cursor: 'pointer', fontSize: 14, textAlign: 'left',
                display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, color: farben.text,
                border: language === lang ? '2px solid #1976d2' : `1px solid ${farben.rand}`,
                background: language === lang ? farben.aktiv : 'transparent',
                fontWeight: language === lang ? 'bold' : 'normal' }}
            >
              <span style={{ fontSize: 18 }}>{flags[lang] || '🌐'}</span>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {languageNames[lang]?.replace(/^[^\s]+\s/, '') || lang}
              </span>
            </button>
          ))}
        </div>
        {treffer.length === 0 && <div data-testid="lang-none" style={{ padding: 12, textAlign: 'center', color: '#999' }}>–</div>}
      </div>
    );
  }

  if (variant === 'dropdown') {
    return (
      <select
        value={language}
        onChange={(e) => setLanguage(e.target.value)}
        style={{
          padding: '8px 12px',
          borderRadius: 6,
          border: '1px solid #ccc',
          fontSize: 14,
          cursor: 'pointer',
          background: '#fff',
          ...style,
        }}
      >
        {availableLanguages.map((lang) => (
          <option key={lang} value={lang}>
            {flags[lang] || '🌐'} {languageNames[lang]}
          </option>
        ))}
      </select>
    );
  }

  if (variant === 'buttons') {
    return (
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', ...style }}>
        {availableLanguages.map((lang) => (
          <button
            key={lang}
            onClick={() => setLanguage(lang)}
            style={{
              padding: '8px 16px',
              borderRadius: 6,
              border: language === lang ? '2px solid #1976d2' : '1px solid #ccc',
              background: language === lang ? '#e3f2fd' : '#fff',
              cursor: 'pointer',
              fontWeight: language === lang ? 'bold' : 'normal',
              fontSize: 14,
            }}
          >
            {flags[lang] || '🌐'} {languageNames[lang]}
          </button>
        ))}
      </div>
    );
  }

  if (variant === 'flags') {
    return (
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', ...style }}>
        {availableLanguages.map((lang) => (
          <button
            key={lang}
            onClick={() => setLanguage(lang)}
            title={languageNames[lang]}
            style={{
              padding: '4px 8px',
              borderRadius: 4,
              border: language === lang ? '2px solid #1976d2' : '1px solid transparent',
              background: language === lang ? '#e3f2fd' : 'transparent',
              cursor: 'pointer',
              fontSize: 20,
            }}
          >
            {flags[lang] || '🌐'}
          </button>
        ))}
      </div>
    );
  }

  return null;
};

export default LanguageSelector;
